import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/shared/api/client';
import type { Schema } from '@/shared/api/contract';
import { ApiError } from '@/shared/api/errors';

/**
 * The key of the session query, here and not in `features/auth`, so a feature that needs
 * to know who is logged in (the archive asks whether the person sees the money) can read
 * the cache without importing another feature. `features/auth` re-exports it.
 */
export const sessionQueryKey = ['auth', 'session'] as const;

export type SessionUser = Schema<'SessionRead'>;

/**
 * The session query, whole: the guard (`useSession` in `features/auth`) fetches with it
 * and every reader below subscribes with it. One set of options and not two, because in
 * TanStack Query v5 the options of the last observer to render become the query's, and
 * those are what `invalidateQueries` refetches with. Until 2026-10-01 the readers said
 * `queryFn: skipToken`; a tab that re-rendered on its own (its mutation finishing) handed
 * the query that `skipToken`, and the refetch after «Guardar buzón» failed: the session
 * stayed old, and a mailbox just assigned did not reach the menu or the Bandeja.
 *
 * A 401 is a valid answer (nobody is logged in), so it is never retried: retrying would
 * delay the redirect to the login screen for no reason.
 */
export const sessionQueryOptions = queryOptions<SessionUser, ApiError>({
  queryKey: sessionQueryKey,
  queryFn: () => api.get<SessionUser>('/api/auth/me'),
  retry: (failureCount, error) =>
    !(error instanceof ApiError && error.isUnauthorized) && failureCount < 2,
  staleTime: 60_000,
});

/**
 * A reader's subscription: the same options, `enabled: false`. It never fetches on its own
 * (the guard did, before any page rendered), and whatever refetches the query still finds
 * the real request in its options.
 */
function useSessionSubscription(): SessionUser | undefined {
  const { data } = useQuery({ ...sessionQueryOptions, enabled: false });
  return data;
}

/**
 * Whoever `RequireAuth` let in, from the cache. Not a subscription: the guard already
 * fetched the session before any page rendered, and a page that asks "does this person
 * see the money?" does not need to re-render when the session refreshes.
 */
export function useCurrentUser(): SessionUser | undefined {
  return useQueryClient().getQueryData<SessionUser>(sessionQueryKey);
}

/** What is switched on for this deployment (fase 1, ADR 0016 of the back). */
export type Features = Schema<'FeaturesRead'>;

/** Off is the safe default: an older back that has not shipped `features` yet, or a
 * session still loading, both read as everything switched off. */
export const DEFAULT_FEATURES: Features = { document_archive: false };

export function featuresOf(user: SessionUser | undefined): Features {
  return { ...DEFAULT_FEATURES, ...user?.features };
}

/**
 * The two tiers of contract v10.11 (v10.13: no role, two switches). `isPlatform`: a
 * superadministrator, Ignira, the provider, who sees the
 * platform half of the panel (statistics, access log, retention, document policy) and the
 * accounts of Ignira. `isManager`: Ignira or anybody with the `is_admin` switch on, who
 * runs the management half (people, mailboxes, destinations, activation). The API sends
 * `is_manager` computed; the fallback is for an older back that has not shipped it.
 */
export function isPlatform(user: SessionUser | undefined): boolean {
  return user?.is_superadmin === true;
}

export function isManager(user: SessionUser | undefined): boolean {
  return user?.is_manager ?? (isPlatform(user) || user?.is_admin === true);
}

/** Subscribing, like `useFeatures`: a shell that hides tabs for the wrong tier re-renders
 * when the session changes. */
export function useIsPlatform(): boolean {
  return isPlatform(useSessionUser());
}

/** The session, subscribed: for a screen whose controls depend on who is looking. */
export function useSessionUser(): SessionUser | undefined {
  return useSessionSubscription();
}

/**
 * Subscribes, unlike `useCurrentUser`: a component that renders differently when a
 * feature is on or off (a nav item, a guard) needs to re-render if the session changes.
 * Reads the query `RequireAuth`'s `useSession` already owns, with its same options
 * (`sessionQueryOptions`) and without fetching on its own.
 */
export function useFeatures(): Features {
  return featuresOf(useSessionSubscription());
}
